import { Doughnut, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Filler,
  Plugin,
} from "chart.js";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { darkAndLightThemeDefault } from "./ThemeComponents";
import { ContentContext } from "../contexts/ContentContext";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  Filler,
);

export const CircularGraph = ({ data, labelColors }) => {
  const { t } = useTranslation("common");
  const { isDarkMode } = useContext(ContentContext);
  const labels = data.map((item) => t(item.label));
  const counts = data.map((item) => item.count);
  const colors = [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#22c55e",
    "#06b6d4",
    "#6366f1",
    "#b45309",
    "#6b7280",
    "#dc2626",
    "#4d7c0f",
  ];

  const chartData = {
    labels,
    datasets: [
      {
        data: counts,
        backgroundColor: labelColors
          ? labels.map((label) => labelColors[label])
          : colors.slice(0, data.length),
        hoverOffset: 7,
        borderWidth: 1,
        weight: 1,
        borderColor: isDarkMode ? "#d1d5db" : "#1f2937",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        // display: false,
        labels: {
          boxWidth: 15, // Width of the legend color box
          padding: 10, // Spacing around each legend item
          color: isDarkMode ? "#d1d5db" : "#1f2937",
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const label = tooltipItem.label || "";
            const value = tooltipItem.raw || 0;
            return `${value} ${t("tickets")}`;
          },
        },
      },
    },
  };

  return (
    // <div style={{ width: "300px", height: "300px", margin: "0 auto" }}>
    <Doughnut data={chartData} options={chartOptions} />
  );
};

export const LineChart = ({ datasets }) => {
  const { t } = useTranslation("common");
  const { isDarkMode } = useContext(ContentContext);

  const [currentDataset, setCurrentDataset] = useState("hours_to_count");

  // Prepare datasets
  const formatData = (data) => {
    let sortedKeys = [];

    if (currentDataset === "hours_to_count") {
      const now = new Date();
      const currentHour = now.getHours();
      const formattedEntries = Object.entries(data).map(([time, count]) => {
        const [hour, minute] = time.split(":").map(Number);
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return { time, hour, count, date };
      });
      const sortedEntries = formattedEntries.sort((a, b) => a.date - b.date);

      // Filter and rearrange entries
      const reorderedEntries = [];
      for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        if (entry.hour === currentHour + 1) {
          reorderedEntries.push(...sortedEntries.slice(i));
          reorderedEntries.push(...sortedEntries.slice(0, i));
          break;
        }
      }
      sortedKeys = reorderedEntries.map((el) => el.time);
    } else if (currentDataset === "months_to_count") {
      const now = new Date();
      const currentMonth = now.getMonth();
      const formattedEntries = Object.entries(data).map(([month, count]) => {
        const parsedMonth = parseInt(month) - 1;
        const date = new Date();
        date.setMonth(parsedMonth);
        return { month, parsedMonth, count, date };
      });
      const sortedEntries = formattedEntries.sort((a, b) => a.date - b.date);

      const reorderedEntries = [];
      for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        if (entry.parsedMonth === currentMonth + 1) {
          reorderedEntries.push(...sortedEntries.slice(i));
          reorderedEntries.push(...sortedEntries.slice(0, i));
          break;
        }
      }
      sortedKeys = reorderedEntries.map((el) => el.month);
    } else {
      sortedKeys = Object.keys(data).sort((a, b) => {
        // Custom sorting logic for numerical or date-like keys
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    // Extract sorted labels and values
    const labels = sortedKeys;
    const values = sortedKeys.map((key) => data[key]);
    return { labels, values };
  };

  const datasetLabels = {
    hours_to_count: t("Last 24 Hours"),
    intervals_to_count: t("Last Week"),
    weeks_to_count: t("Last Month"),
    months_to_count: t("Last Year"),
  };

  const datasetTitles = {
    hours_to_count: t("Hours"),
    intervals_to_count: t("Hours"),
    weeks_to_count: t("Days"),
    months_to_count: t("Months"),
  };

  const monthsLookup = {
    "01": t("January"),
    "02": t("February"),
    "03": t("March"),
    "04": t("April"),
    "05": t("May"),
    "06": t("June"),
    "07": t("July"),
    "08": t("August"),
    "09": t("September"),
    10: t("October"),
    11: t("November"),
    12: t("December"),
  };

  const { labels, values } = formatData(datasets[currentDataset]);

  const chartData = {
    labels,
    datasets: [
      {
        label: datasetLabels[currentDataset],
        data: values,
        borderColor: ["#36A2EB"],
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        fill: true,
        tension: 0.2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        color: isDarkMode ? "#d1d5db" : "#1f2937",
      },
      tooltip: {
        mode: "index", // Tooltip shows all values along a vertical line
        intersect: false, // Works even when not hovering over a data point
        callbacks: {
          title: (tooltipItem) => {
            const value = tooltipItem[0].label || " ";
            if (currentDataset === "months_to_count") {
              return `${monthsLookup[value] ?? value}`;
            }
            return `${value}`;
          },
          label: (tooltipItem) => {
            const value = tooltipItem.raw || 0;
            return `${value} ${t("tickets")}`;
          },
        },
      },
    },
    interaction: {
      mode: "index", // Interaction highlights all points along the X-axis
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: datasetTitles[currentDataset],
          color: isDarkMode ? "#d1d5db" : "#1f2937",
        },
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#1f2937",
          callback: function (value, index) {
            // Only display every 2nd label (adjust `2` to your desired interval)
            if (currentDataset === "intervals_to_count") {
              return index % 4 === 0
                ? this.getLabelForValue(value).split(" ")[0]
                : "";
            } else if (currentDataset === "months_to_count") {
              return monthsLookup[this.getLabelForValue(value)];
            } else {
              return this.getLabelForValue(value);
            }
          },
        },
      },
      y: {
        title: {
          display: true,
          text: t("Tickets"),
          color: isDarkMode ? "#d1d5db" : "#1f2937",
        },
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#1f2937",
          callback: function (value) {
            // Only display integers
            return Number.isInteger(value) ? value : null;
          },
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="h-[300px] w-full flex flex-col gap-3">
      <div className="flex gap-1">
        {Object.keys(datasetLabels).map((ds, index) => (
          <button
            key={index}
            className={`btn btn-sm ${currentDataset === ds ? "hover:bg-blue-500 text-white bg-primary" : darkAndLightThemeDefault}`}
            onClick={() => setCurrentDataset(ds)}
          >
            {datasetLabels[ds]}
          </button>
        ))}
      </div>
      <Line id="chart" data={chartData} options={chartOptions} />
    </div>
  );
};

export const LineChartTwoLines = ({ datasets }) => {
  const { t } = useTranslation("common");
  const { isDarkMode } = useContext(ContentContext);

  const [currentDataset, setCurrentDataset] = useState("hours_to_count");

  // Prepare datasets
  const formatData = (data) => {
    let sortedKeys = [];

    if (currentDataset === "hours_to_count") {
      const now = new Date();
      const currentHour = now.getHours();
      const formattedEntries = Object.entries(data).map(([time, count]) => {
        const [hour, minute] = time.split(":").map(Number);
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return { time, hour, count, date };
      });
      const sortedEntries = formattedEntries.sort((a, b) => a.date - b.date);

      // Filter and rearrange entries
      const reorderedEntries = [];
      for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        if (entry.hour === currentHour + 1) {
          reorderedEntries.push(...sortedEntries.slice(i));
          reorderedEntries.push(...sortedEntries.slice(0, i));
          break;
        }
      }
      sortedKeys = reorderedEntries.map((el) => el.time);
    } else if (currentDataset === "months_to_count") {
      const now = new Date();
      const currentMonth = now.getMonth();
      const formattedEntries = Object.entries(data).map(([month, count]) => {
        const parsedMonth = parseInt(month) - 1;
        const date = new Date();
        date.setMonth(parsedMonth);
        return { month, parsedMonth, count, date };
      });
      const sortedEntries = formattedEntries.sort((a, b) => a.date - b.date);

      const reorderedEntries = [];
      for (let i = 0; i < sortedEntries.length; i++) {
        const entry = sortedEntries[i];
        if (entry.parsedMonth === currentMonth + 1) {
          reorderedEntries.push(...sortedEntries.slice(i));
          reorderedEntries.push(...sortedEntries.slice(0, i));
          break;
        }
      }
      sortedKeys = reorderedEntries.map((el) => el.month);
    } else {
      sortedKeys = Object.keys(data).sort((a, b) => {
        // Custom sorting logic for numerical or date-like keys
        return a.localeCompare(b, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    }

    // Extract sorted labels and values
    const labels = sortedKeys;
    const values = sortedKeys.map((key) => data[key]);
    return { labels, values };
  };

  const datasetLabels = {
    hours_to_count: t("Last 24 Hours"),
    intervals_to_count: t("Last Week"),
    weeks_to_count: t("Last Month"),
    months_to_count: t("Last Year"),
  };

  const datasetTitles = {
    hours_to_count: t("Hours"),
    intervals_to_count: t("Hours"),
    weeks_to_count: t("Days"),
    months_to_count: t("Months"),
  };

  const monthsLookup = {
    "01": t("January"),
    "02": t("February"),
    "03": t("March"),
    "04": t("April"),
    "05": t("May"),
    "06": t("June"),
    "07": t("July"),
    "08": t("August"),
    "09": t("September"),
    10: t("October"),
    11: t("November"),
    12: t("December"),
  };

  const { labels, values } = formatData(datasets[currentDataset]);

  const chartData = {
    labels,
    datasets: [
      {
        label: t("Mean"),
        data: values.map((v) => v[0]),
        borderColor: ["#36A2EB"],
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        fill: true,
        tension: 0.2,
      },
      {
        label: t("Median"),
        data: values.map((v) => v[1]),
        borderColor: ["#fde047"],
        backgroundColor: "rgba(253, 224, 71, 0.2)",
        fill: true,
        tension: 0.2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        color: isDarkMode ? "#d1d5db" : "#1f2937",
      },
      tooltip: {
        mode: "index", // Tooltip shows all values along a vertical line
        intersect: false, // Works even when not hovering over a data point
        callbacks: {
          title: (tooltipItem) => {
            const value = tooltipItem[0].label || " ";
            if (currentDataset === "months_to_count") {
              return `${monthsLookup[value] ?? value}`;
            }
            return `${value}`;
          },
          label: (tooltipItem) => {
            const value = tooltipItem.raw || 0;
            return `${value} ${t("minutes")}`;
          },
        },
      },
    },
    interaction: {
      mode: "index", // Interaction highlights all points along the X-axis
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: datasetTitles[currentDataset],
          color: isDarkMode ? "#d1d5db" : "#1f2937",
        },
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#1f2937",
          callback: function (value, index) {
            // Only display every 2nd label (adjust `2` to your desired interval)
            if (currentDataset === "intervals_to_count") {
              return index % 4 === 0
                ? this.getLabelForValue(value).split(" ")[0]
                : "";
            } else if (currentDataset === "months_to_count") {
              return monthsLookup[this.getLabelForValue(value)];
            } else {
              return this.getLabelForValue(value);
            }
          },
        },
      },
      y: {
        title: {
          display: true,
          text: t("Minutes"),
          color: isDarkMode ? "#d1d5db" : "#1f2937",
        },
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#1f2937",
          callback: function (value) {
            // Only display integers
            return Number.isInteger(value) ? value : null;
          },
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="h-[300px] w-full flex flex-col gap-3">
      <div className="flex gap-1">
        {Object.keys(datasetLabels).map((ds, index) => (
          <button
            key={index}
            className={`btn btn-sm ${currentDataset === ds ? "hover:bg-blue-500 text-white bg-primary" : darkAndLightThemeDefault}`}
            onClick={() => setCurrentDataset(ds)}
          >
            {datasetLabels[ds]}
          </button>
        ))}
      </div>
      <Line id="chart" data={chartData} options={chartOptions} />
    </div>
  );
};
